import api from './api';

export interface RAGPrepareRequest {
  force_reindex?: boolean;
}

export interface RAGCostEstimate {
  total_documents: number;
  documents_to_process: number;
  skipped_documents: number;
  not_preparable_documents: number;
  estimated_chunks: number;
  estimated_embedding_tokens: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string | null;
  llm_category: string;
  pricing_warning: string | null;
}

export interface RAGPrepareResponse {
  status: string;
  job_id: string;
  message: string;
}

export interface RAGPreparationProgress {
  total_documents: number;
  processed_documents: number;
  percentage: number;
}

export interface RAGStatusResponse {
  status: string;
  progress?: RAGPreparationProgress | null;
  estimated_completion?: string | null;
  job_id?: string | null;
  message?: string | null;

  vector_index_status?: string | null;
  vector_index_store?: string | null;
  vector_index_started_at?: string | null;
  vector_index_completed_at?: string | null;
  vector_index_error?: string | null;

  bm25_index_status?: string | null;
  bm25_index_store?: string | null;
  bm25_index_started_at?: string | null;
  bm25_index_completed_at?: string | null;
  bm25_index_total_chunks?: number | null;
  bm25_index_error?: string | null;
}

export interface RAGIndexStats {
  status?: string | null;
  store?: string | null;
  started_at?: string | null;
  completed_at?: string | null;

  total_chunks?: number | null;
  indexed_chunks?: number | null;

  vocabulary_size?: number | null;
  avg_doc_length?: number | null;

  reason?: string | null;
  error?: string | null;
}

export interface RAGDocChunkStats {
  docs_with_chunks: number;
  min_chunks?: number | null;
  max_chunks?: number | null;
  avg_chunks?: number | null;
  median_chunks_approx?: number | null;
  histogram?: Record<string, number> | null;
}

export interface RAGStatsResponse {
  kb_id: string;
  status?: string | null;
  document_count?: number | null;
  prepared_document_count?: number | null;
  missing_markdown_document_count?: number | null;
  chunk_count?: number | null;
  prepared_at?: string | null;
  updated_at?: string | null;
  vector_index?: RAGIndexStats | null;
  bm25_index?: RAGIndexStats | null;
  chunking_strategy?: string | null;
  chunking_max_tokens?: number | null;
  chunking_min_tokens?: number | null;
  chunking_overlap_tokens?: number | null;
  chunking_token_method?: string | null;
  per_doc_chunk_stats?: RAGDocChunkStats | null;
  embedding_model_id?: string | null;
  embedding_dimensions?: number | null;
  embedding_vector_store?: string | null;
  embedding_distance_metric?: string | null;
  last_job_completed_at?: string | null;
  last_job_duration_seconds?: number | null;
  last_job_force_reindex?: boolean | null;
}

export interface LKBReadinessResponse {
  kb_id: string;
  is_ready: boolean;
  reasons: string[];
  visibility?: string | null;
  kb_class?: string | null;
  document_count: number;
  sample_size: number;
  with_text_sample: number;
  estimated_docs_per_sec: number;
  estimated_seconds: number;
}

export interface LKBStatusResponse {
  kb_id: string;
  status: string; // not_prepared | preparing | ready | failed
  mode?: string | null; // full | incremental
  total_documents: number;
  processed_documents: number;
  percentage: number;
  docs_per_sec?: number | null;
  current_window?: number | null;
  peak_rss_mb?: number | null;
  reembed_all?: boolean | null;
  model?: string | null;
  vocabulary_size?: number | null;
  latency_ms?: number | null;
  message?: string | null;
  error_message?: string | null;
  stalled: boolean;
  started_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
}

export interface LKBBuildResponse {
  status: string;
  message: string;
  launched: boolean;
}

export interface RAGQueryRequest {
  query: string;
  kb_id?: string;

  top_k?: number;
  top_n?: number;
  search_mode?: 'hybrid' | 'vector_only' | 'bm25_only' | string;
  enable_reranking?: boolean;

  section_filter?: string | null;
  document_filter?: string[] | null;
}

export interface RAGChunkResult {
  chunk_id: string;
  document_id: string;
  section_type?: string | null;
  section_title?: string | null;
  text: string;
  highlights?: string[] | null;
  chunk_index?: number | null;
  page_number?: number | null;
}

export interface RAGDocumentResult {
  document_id: string;
  document_title?: string | null;
  document_type?: string | null;
  relevance_score: number;
  score_breakdown: Record<string, unknown>;
  chunks: RAGChunkResult[];
}

export interface RAGSearchMetadata {
  total_results: number;
  search_time_ms: number;
  semantic_candidates: number;
  bm25_candidates: number;
  fusion_candidates: number;
  warnings?: string[];
}

export interface RAGQueryResponse {
  query_id: string;
  query: string;
  kb_id: string;
  results: RAGDocumentResult[];
  metadata: RAGSearchMetadata;
  search_mode: string;
  reranking_applied: boolean;
  latency_ms: number;
  debug?: Record<string, unknown> | null;
}

// Chunk Position types
export interface ScaledRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
  pageNumber: number;
}

export interface ChunkPositionData {
  boundingRect: ScaledRect;
  rects: ScaledRect[];
  pageNumber: number;
}

export interface ChunkPositionRecord {
  chunk_id: string;
  document_id: string;
  position: ChunkPositionData;
  computed_at?: string | null;
}

export interface ChunkPositionBatchRequest {
  document_id: string;
  positions: Array<{
    chunk_id: string;
    position: ChunkPositionData;
  }>;
}

export interface ChunkPositionResponse {
  document_id: string;
  positions: ChunkPositionRecord[];
  total: number;
}

export const ragService = {
  async prepare(kbId: string, opts: RAGPrepareRequest = {}): Promise<RAGPrepareResponse> {
    const response = await api.post<RAGPrepareResponse>(`/knowledge-bases/${kbId}/rag/prepare`, {
      force_reindex: Boolean(opts.force_reindex),
    });
    return response.data;
  },

  async adminReindex(kbId: string): Promise<RAGPrepareResponse> {
    const response = await api.post<RAGPrepareResponse>(`/knowledge-bases/${kbId}/rag/admin/reindex`);
    return response.data;
  },

  async estimate(kbId: string, options?: { forceReindex?: boolean }): Promise<RAGCostEstimate> {
    const response = await api.post<RAGCostEstimate>(`/knowledge-bases/${kbId}/rag/estimate`, { force_reindex: Boolean(options?.forceReindex) });
    return response.data;
  },

  async status(kbId: string): Promise<RAGStatusResponse> {
    const response = await api.get<RAGStatusResponse>(`/knowledge-bases/${kbId}/rag/status`);
    return response.data;
  },

  async stats(kbId: string): Promise<RAGStatsResponse> {
    const response = await api.get<RAGStatsResponse>(`/knowledge-bases/${kbId}/rag/stats`);
    return response.data;
  },

  async query(kbId: string, payload: RAGQueryRequest): Promise<RAGQueryResponse> {
    const response = await api.post<RAGQueryResponse>(`/knowledge-bases/${kbId}/rag/query`, payload);
    return response.data;
  },

  // LKB (reference_large) — SysAdmin only: readiness, live progress polling, and the build trigger.
  async lkbReadiness(kbId: string): Promise<LKBReadinessResponse> {
    const response = await api.get<LKBReadinessResponse>(`/knowledge-bases/${kbId}/rag/lkb/readiness`);
    return response.data;
  },

  async lkbStatus(kbId: string): Promise<LKBStatusResponse> {
    const response = await api.get<LKBStatusResponse>(`/knowledge-bases/${kbId}/rag/lkb/status`);
    return response.data;
  },

  // One-click full LKB (re)build: the backend runs it in-process as a FastAPI background task.
  async lkbBuild(kbId: string, options?: { forceReindex?: boolean }): Promise<LKBBuildResponse> {
    const response = await api.post<LKBBuildResponse>(`/knowledge-bases/${kbId}/rag/lkb/build`, {
      force_reindex: Boolean(options?.forceReindex),
    });
    return response.data;
  },

  // Chunk Position methods
  async saveChunkPositions(kbId: string, payload: ChunkPositionBatchRequest): Promise<{ saved: number; total: number }> {
    const response = await api.post<{ saved: number; total: number }>(`/knowledge-bases/${kbId}/rag/chunk-positions`, payload);
    return response.data;
  },

  async getChunkPositions(kbId: string, documentId: string): Promise<ChunkPositionResponse> {
    const response = await api.get<ChunkPositionResponse>(`/knowledge-bases/${kbId}/documents/${documentId}/chunk-positions`);
    return response.data;
  },

  async deleteChunkPositions(kbId: string, documentId: string): Promise<{ deleted: number }> {
    const response = await api.delete<{ deleted: number }>(`/knowledge-bases/${kbId}/documents/${documentId}/chunk-positions`);
    return response.data;
  },
};
